<template>
    <Breadcrumb v-if="shouldShowBreadcrumbs" :class="class">
        <BreadcrumbList>
            <!-- Custom prefix slot for additional components -->
            <slot name="prefix" />
            
            <template v-for="(item, index) in finalBreadcrumbs" :key="item.name || index">
                <BreadcrumbItem :class="item.isActive ? 'text-foreground' : ''">
                    <!-- Custom component for this breadcrumb item -->
                    <component
                        v-if="item.component"
                        :is="item.component"
                        v-bind="item.props"
                        :item="item"
                    >
                        <template v-for="(slotContent, slotName) in item.slots" :key="slotName" #[slotName]>
                            <component :is="slotContent" :item="item" />
                        </template>
                    </component>
                    
                    <!-- Active page (non-clickable) -->
                    <BreadcrumbPage v-else-if="item.isActive" class="flex items-center gap-1.5">
                        <!-- Icon slot with fallback -->
                        <slot name="icon" :item="item">
                            <component v-if="item.icon" :is="item.icon" class="h-4 w-4" />
                        </slot>
                        
                        <!-- Label slot with fallback -->
                        <slot name="label" :item="item">
                            <span class="truncate max-w-60" :title="item.label">{{ item.label }}</span>
                        </slot>
                        
                        <!-- Active page suffix slot -->
                        <slot name="active-suffix" :item="item" />
                    </BreadcrumbPage>
                    
                    <!-- Clickable breadcrumb link -->
                    <BreadcrumbLink
                        v-else-if="item.isClickable"
                        href="#"
                        @click.prevent="navigateTo(item)"
                        class="flex items-center gap-1.5"
                    >
                        <!-- Icon slot with fallback -->
                        <slot name="icon" :item="item">
                            <component v-if="item.icon" :is="item.icon" class="h-4 w-4" />
                        </slot>
                        
                        <!-- Label slot with fallback -->
                        <slot name="label" :item="item">
                            <span class="truncate max-w-60" :title="item.label">{{ item.label }}</span>
                        </slot>
                    </BreadcrumbLink>
                    
                    <!-- Non-clickable breadcrumb (fallback) -->
                    <span v-else class="flex items-center gap-1.5 text-muted-foreground">
                        <!-- Icon slot with fallback -->
                        <slot name="icon" :item="item">
                            <component v-if="item.icon" :is="item.icon" class="h-4 w-4" />
                        </slot>
                        
                        <!-- Label slot with fallback -->
                        <slot name="label" :item="item">
                            <span class="truncate max-w-60" :title="item.label">{{ item.label }}</span>
                        </slot>
                    </span>
                </BreadcrumbItem>
                
                <!-- Separator (not shown after last item) -->
                <BreadcrumbSeparator v-if="index < finalBreadcrumbs.length - 1" />
            </template>
            
            <!-- Custom suffix slot for additional components -->
            <slot name="suffix" />
        </BreadcrumbList>
    </Breadcrumb>
</template>

<script setup>
import { computed } from 'vue';
import {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';

const props = defineProps({
    /**
     * Additional CSS classes for the breadcrumb container
     */
    class: {
        type: String,
        default: ''
    },
    /**
     * Override breadcrumbs with custom items
     * Useful for special pages or testing
     */
    customBreadcrumbs: {
        type: Array,
        default: null
    }
});

const { breadcrumbs, navigateTo, shouldShowBreadcrumbs } = useBreadcrumbs();

// Use custom breadcrumbs if provided, otherwise use dynamic ones
const finalBreadcrumbs = computed(() => props.customBreadcrumbs || breadcrumbs.value);
</script>